import { useContext, useEffect, useRef } from 'react'
import { classes, style, stylesheet } from 'typestyle'
import { Link, useLocation, useParams } from 'react-router-dom'
import { isObject } from 'lodash'

import config from '../../../../config'
import { withRouter } from '../../../contexts/with-router'

/* Core ========================================================================================= */
import { addSnippet } from '../../../core/snippet-manager'
import { GLOBAL_CONTENTS } from '../../../common/global-contents'
import { get } from '../../../share/find'
import { formatDate } from '../../../share/format'

/* Context ====================================================================================== */
import { NavigationContext } from '../../../contexts/navigation'
import { CacheContext } from '../../../contexts/cache-context'

/* Hooks ======================================================================================== */
import useBoolean from '../../../hooks/use-boolean'
import useFetch from '../../../hooks/use-fetch'
import useLocal from '../../../hooks/use-local'
import useValue from '../../../hooks/use-value'

/* Components =================================================================================== */
import AppLayout from '../../routes/app/layout'
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { Dropdown } from '../../../components/dropdown'
import { EvenMoreSimplerPagination } from '../../../modules/navigation/pagination'
import { Line } from '../../../components/line'
import { Select } from '../../../components/select'
import { Sidebar } from '../../../components/sidebar'
import { Snippet } from '../../../modules/layouts/snippet'
import { Table } from '../../../components/table'
import { Text } from '../../../components/text/text.component'
import { TextField } from '../../../components/text-field'

/* Styles ======================================================================================= */
import { f16i } from '../../../styles/styleset/font-size/f16i'
import { f18i } from '../../../styles/styleset/font-size/f18i'
import { flat } from '../../../share/obj-equal'
import { flex } from '../../../styles/styleset/flex/flex'
import { flexmd } from '../../../styles/styleset/flex/flexmd'
import { hide } from '../../../styles/styleset/hide/hide'
import { hidemdi } from '../../../styles/styleset/hide/hidemdi'
import { mb4 } from '../../../styles/styleset/margin/mb4'
import { mb8 } from '../../../styles/styleset/margin/mb8'
import { ml4 } from '../../../styles/styleset/margin/ml4'
import { mr4mdi } from '../../../styles/styleset/margin/mr4mdi'
import { mr8 } from '../../../styles/styleset/margin/mr8'
import { mt8 } from '../../../styles/styleset/margin/mt8'
import { p16 } from '../../../styles/styleset/padding/p16'
import { p4 } from '../../../styles/styleset/padding/p4'
import { p8 } from '../../../styles/styleset/padding/p8'
import { pb32 } from '../../../styles/styleset/padding/pb32'
import { pl16 } from '../../../styles/styleset/padding/pl16'
import { pr8 } from '../../../styles/styleset/padding/pr8'
import { pt24 } from '../../../styles/styleset/padding/pt24'
import { w100 } from '../../../styles/styleset/width/w100'
import { f12i } from '../../../styles/styleset/font-size/f12i'
import { Circle } from '../../../components/circle'
import { background } from 'csx'

const SEARCH = {
  /**
   * Default query will not affect url search parameters
   */
  default: {
    limit: 10,
    page: 1,
  },
}

export default addSnippet(
  'TableView',
  withRouter((props: any) => {
    const { trigger, sync, isEditable, isViewable = true } = props

    const [local] = useLocal()
    const { navigation, retrigger } = useContext(NavigationContext)
    const location = useLocation()
    const params = useParams()

    const url = `${process.env.REACT_APP_API_URL}${location.pathname}`
    const headers = get('payload.base.table.headers', navigation, 'array')
    const actions = get('payload.base.table.actions', navigation, 'array')
    const name = get('payload.base.name', navigation, 'object')
    const base = get('payload.base', navigation, 'object')
    const filters = get('table.filters', base, 'array')
    const lookups = get('table.lookups', base, 'array')

    const [sort, $sort] = useValue({})
    const [sortTrigger, $sortTrigger] = useValue()
    const [, $loaded] = useBoolean(false)
    const [searchToggled] = useBoolean(false)
    const [equalTerms, $equalTerms] = useValue({})
    const [searchTerms, $searchTerms] = useValue({})
    const [postSearchTerms, $postSearchTerms] = useValue({})
    const searchTermValue = useRef<string>('')
    const [searchTrigger, $searchTrigger] = useValue()
    const [selected, $selected] = useValue()

    const cacheContext = useContext(CacheContext)
    const appLayoutContext = useContext(AppLayout.context)
    const { getOffsetTop = () => 0 } = appLayoutContext || ({} as any)

    const [rows, $rows] = useFetch<{
      count: number
      data: any[]
      page: number
      totalPage: number
    }>(
      {
        count: 0,
        data: [],
        page: 1,
        totalPage: 1,
      },
      {
        url,
        search: SEARCH.default,
        location: location,
        updateGetParams: false,
        isLoading: true,
        onLocationChange: (props: any) => {
          const { url, encodedParam } = props

          window.history.replaceState(
            null,
            '',
            `${url.replace(
              process.env.REACT_APP_API_URL,
              process.env.REACT_APP_WEB_URL,
            )}?${encodedParam}`,
          )
        },
      },
    )

    const [row, $row] = useFetch(
      {},
      {
        url,
      },
    )

    const [internalHeaders, $internalHeaders] = useValue(headers)
    const [defaultSearch, $defaultSearch] = useValue()

    useEffect(() => {
      if (params.parentRowId) {
        cacheContext?.$value?.set?.(prev => {
          delete prev.row
          prev.parentId = params.parentRowId
          return { ...prev }
        })
      }
    }, [params])

    useEffect(() => {
      if (Object.keys(base).length > 0 && rows.mounted) {
        const search: any = { equal: {} }

        const defaultSort = filters.filter((a: any) => a.type === 'sort')[0]
        if (defaultSort) {
          search['sort'] = {
            key: defaultSort.value.name,
            direction: defaultSort.value.direction,
          }
        }

        filters
          .filter((a: any) => a.type === 'select')
          .map((filter: any) => {
            if (filter?.value?.name) {
              search.equal[filter.name] = filter.value.name
              $equalTerms.set((prev: any) => {
                return {
                  ...prev,
                  [filter.name]: filter.value.name,
                }
              })
            }
          })

        const defaultSearch = {
          ...search,
          lookups,
          limit: base.table.limit,
        }

        $rows
          .get({
            location: location,
            search: defaultSearch,
          })
          .then((payload: any) => {
            $loaded.set(true)
            props.$rows?.set?.(payload)

            props.sync()
            $rows.$payload.set?.(payload)
          })

        if (rows.search.search) {
          const searchTermFlatten = flat(rows.search.search)

          searchTermValue.current = searchTermFlatten[Object.keys(searchTermFlatten)[0]]
        }

        if (rows.search.sort) {
          $sort.set({
            ...filters
              .filter((a: any) => a.type === 'sort')?.[0]
              .options.filter((a: any) => a.name === rows.search.sort.key)[0],
            direction: rows.search.sort.direction,
          })
        } else if (filters.filter((a: any) => a.type === 'sort')?.[0]) {
          $sort.set({
            ...filters.filter((a: any) => a.type === 'sort')?.[0]?.value,
            initial: true,
          })
        }

        $defaultSearch.set(defaultSearch)
      }
    }, [navigation.payload, rows.mounted])

    useEffect(() => {
      if (retrigger) {
        $rows.get().then((payload: any) => {
          $loaded.set(true)
          props.$rows?.set?.(payload)

          props.sync()
          $rows.$payload.set?.(payload)
        })
      }
    }, [retrigger])

    useEffect(() => {
      if (trigger) {
        $rows.get().then((payload: any) => {
          $loaded.set(true)
          props.$rows?.set?.(payload)

          props.sync()
          $rows.$payload.set?.(payload)
        })
      }
    }, [trigger])

    useEffect(() => {
      let unlisten: any

      return () => {
        if (unlisten) unlisten()
      }
    }, [defaultSearch, rows.loaded])

    useEffect(() => {
      if (headers.length > 0) {
        const hasLink = headers.some(h => h.link)
        $internalHeaders.set(
          headers.map((header: any, index: number) => {
            return {
              ...header,
              ...(header.type !== 'swatch'
                ? {
                    renderColumn: (props: any) => {
                      const {
                        column,
                        columnKey,
                        row,
                        disableHeight,
                        translate,
                        link = index === 0 && !hasLink,
                      } = props

                      let value =
                        translate && config.dictionary?.[column]?.[local]
                          ? config.dictionary?.[column]?.[local]
                          : column

                      switch (header.type) {
                        case 'date':
                          value = formatDate(value, { time: false })
                          break
                        case 'order':
                          value += 1
                          break
                      }

                      return (
                        <Box
                          column
                          alignCenterY
                          baseline="none"
                          fullHeight={!disableHeight}
                          key={columnKey}
                        >
                          <Box row baseline="none">
                            {column ? (
                              <Text
                                baseline="surface"
                                className={row[header.subLabel] && mb4}
                                size="sm"
                                shrink
                                link={link}
                              >
                                {link ? <Link to={row.to}>{value}</Link> : value}
                              </Text>
                            ) : (
                              '-'
                            )}
                            {row[header.subLabel] ? (
                              <Text size="xs" baseline="surface">
                                {row[header.subLabel] ? row[header.subLabel] : ''}
                              </Text>
                            ) : (
                              ''
                            )}
                          </Box>
                        </Box>
                      )
                    },
                  }
                : {}),
            }
          }),
        )
      }
    }, [headers, rows.search])

    useEffect(() => {
      if (sortTrigger) {
        delete rows.search.sort

        $rows
          .get({
            search: {
              ...rows.search,
              sort: {
                key: sort.name,
                direction: sort.direction,
              },
            },
            updateGetParams: !sort.initial,
          })
          .then((payload: any) => {
            $loaded.set(true)
            props.$rows?.set?.(payload)

            props.sync()
            $rows.$payload.set?.(payload)
          })

        $sortTrigger.set(null)
      }
    }, [sortTrigger])

    useEffect(() => {
      if (searchTrigger) {
        $rows
          .get({
            resetSearch:
              Object.keys(searchTerms).length === 0 && Object.keys(equalTerms).length === 0,
            search: {
              ...defaultSearch,
              page: 1,
              search: searchTerms,
              postSearch: postSearchTerms,
              equal: equalTerms,
            },
            updateGetParams: true,
          })
          .then((payload: any) => {
            $loaded.set(true)
            props.$rows?.set?.(payload)

            props.sync()
            $rows.$payload.set?.(payload)
          })

        $searchTrigger.set(null)
      }
    }, [searchTerms, postSearchTerms, equalTerms, searchTrigger])

    const handleSearch = (value: any) => {
      searchTermValue.current = value
    }

    const triggerSearch = () => {
      const searchKeys = filters.filter((a: any) => a.type === 'search')[0]?.keys || []
      const postSearchKeys = filters.filter((a: any) => a.type === 'post-search')[0]?.keys || []
      const search: any = {}
      const postSearch: any = {}

      searchKeys.map((key: string) => {
        search[key] = searchTermValue.current
      })

      postSearchKeys.map((key: string) => {
        postSearch[key] = searchTermValue.current
      })

      $searchTerms.set(search)
      $postSearchTerms.set(postSearch)

      $searchTrigger.set(Date.now())
    }

    const resetSearch = () => {
      searchTermValue.current = ''
      const search: any = {}

      $equalTerms.set(search)
      $searchTerms.set(search)
      $postSearchTerms.set(search)
      $searchTrigger.set(Date.now())
    }

    const renderFilters = (isRow?: boolean) => (
      <Box className="no-print" flex row={isRow}>
        <Dropdown
          floatProps={{
            fullWidth: false,
            flip: false,
          }}
          className={isRow ? mb8 : mr8}
        >
          <Button
            className={w100}
            icon={
              <span className={classes('material-icons', f16i)}>
                {searchToggled ? 'close' : 'search'}
              </span>
            }
            label={GLOBAL_CONTENTS.search[local]}
            size="md"
            square={isRow}
            baseline={searchTermValue ? 'secondary' : 'background'}
          />
          {(close: any) => {
            return (
              <Box
                baseline="surface"
                className={p4}
                shadow
                flex
                alignCenterY
                fullWidth
                style={{
                  maxWidth: 240,
                }}
              >
                <TextField
                  baseline="background"
                  size="sm"
                  placeholder={GLOBAL_CONTENTS.searchDesc[local]}
                  value={searchTermValue.current}
                  onChange={handleSearch}
                  onKeyDown={(e: any) => {
                    if (e.keyCode === 13) {
                      close()
                      triggerSearch()
                    } else if (e.keyCode === 27) {
                      resetSearch()
                    }
                  }}
                  autoFocus
                  alignRight
                >
                  <Button
                    label={GLOBAL_CONTENTS.searchShort[local]}
                    baseline="secondary"
                    size="xs"
                    className={classes(ml4, mr8)}
                    onClick={() => {
                      close()
                      triggerSearch()
                    }}
                    square
                  />
                </TextField>
              </Box>
            )
          }}
        </Dropdown>
        {isRow && (
          <Box row>
            <Line className={mb8} />
          </Box>
        )}
        {filters.filter((a: any) => a.type === 'sort')[0]?.options?.length > 0 && (
          <Box className={isRow ? mb8 : mr8}>
            <Select
              square={isRow}
              size="md"
              flip
              fullWidth
              baseline="background"
              disableIcon={true}
              renderOption={(data: any) => {
                return (
                  <Box
                    baseline="none"
                    className={classes(
                      p8,
                      style({
                        $nest: {
                          '&:hover': {
                            background: 'rgb(240,240,240)',
                            cursor: 'pointer',
                          },
                        },
                      }),
                    )}
                    style={{ whiteSpace: 'nowrap' }}
                    flex
                    alignCenterY
                    fullWidth
                  >
                    <Text size="sm" disableMediaQuery shrink>
                      {data.label}
                    </Text>
                    {sort.name === data.name &&
                      (sort.direction === 1 ? (
                        <span
                          className={classes(
                            process.env.REACT_APP_GOOGLE_MATERIAL_ICONS,
                            ml4,
                            f12i,
                          )}
                        >
                          arrow_upward
                        </span>
                      ) : (
                        <span
                          className={classes(
                            process.env.REACT_APP_GOOGLE_MATERIAL_ICONS,
                            ml4,
                            f12i,
                          )}
                        >
                          arrow_downward
                        </span>
                      ))}
                  </Box>
                )
              }}
              renderValue={(data: any) => {
                return (
                  <Box baseline="none" flex alignCenterY fullWidth>
                    <Box flex alignCenterY baseline="none" fullWidth spaceBetween>
                      <Text
                        className={classes(mr8, style({ userSelect: 'none' }))}
                        baseline="surface"
                        size="xs"
                        disableMediaQuery
                        lighter
                        shrink
                      >
                        {GLOBAL_CONTENTS.sortBy[local]}
                      </Text>
                      <Text size="sm" disableMediaQuery shrink>
                        {data.label || (!isObject(data) && data)}
                      </Text>
                      {sort.direction === 1 ? (
                        <span
                          className={classes(
                            process.env.REACT_APP_GOOGLE_MATERIAL_ICONS,
                            ml4,
                            f12i,
                          )}
                        >
                          arrow_upward
                        </span>
                      ) : (
                        <span
                          className={classes(
                            process.env.REACT_APP_GOOGLE_MATERIAL_ICONS,
                            ml4,
                            f12i,
                          )}
                        >
                          arrow_downward
                        </span>
                      )}
                    </Box>
                  </Box>
                )
              }}
              options={filters.filter((a: any) => a.type === 'sort')[0]?.options}
              onChange={(data: any) => {
                if (sort && sort.name === data.name) {
                  $sort.set({ ...data, direction: sort.direction * -1 })
                } else {
                  $sort.set({ ...data, direction: 1 })
                }

                $sortTrigger.set(Date.now())
              }}
              value={sort}
            />
          </Box>
        )}
        {filters
          .filter((a: any) => a.type === 'select')
          .map((filter: any, key: any) => {
            let value = {
              label: get(`search.equal.${filter.name}`, rows, 'string', ''),
            }

            const tableColorCodes = base?.properties?.tableColorCode
            const colors: any = tableColorCodes?.key === filter.name ? tableColorCodes.options : {}

            if (!value.label) {
              value = filter.value
            }

            return (
              <Box className={isRow ? mb8 : mr8} key={key}>
                <Select
                  square={isRow}
                  size="md"
                  fullWidth
                  flip
                  baseline="background"
                  disableIcon={true}
                  renderOption={(data: any) => {
                    return (
                      <Box
                        baseline="none"
                        className={classes(
                          p8,
                          style({
                            $nest: {
                              '&:hover': {
                                background: 'rgb(240,240,240)',
                                cursor: 'pointer',
                              },
                            },
                          }),
                        )}
                        style={{ whiteSpace: 'nowrap' }}
                        flex
                        alignCenterY
                        fullWidth
                        relative
                      >
                        <Box
                          style={{
                            background: colors[data.label],
                            left: 0,
                            top: 0,
                            width: 2,
                            height: '100%',
                          }}
                          absolute
                        />
                        <Text
                          size="sm"
                          disableMediaQuery
                          shrink
                          style={{ display: 'flex', gap: 4, justifyContent: 'space-between' }}
                        >
                          {data.label}
                        </Text>
                        {sort.name === data.name &&
                          (sort.direction === 1 ? (
                            <span
                              className={classes(
                                process.env.REACT_APP_GOOGLE_MATERIAL_ICONS,
                                ml4,
                                f12i,
                              )}
                            >
                              arrow_upward
                            </span>
                          ) : (
                            <span
                              className={classes(
                                process.env.REACT_APP_GOOGLE_MATERIAL_ICONS,
                                ml4,
                                f12i,
                              )}
                            >
                              arrow_downward
                            </span>
                          ))}
                      </Box>
                    )
                  }}
                  renderPlaceholder={() => {
                    return (
                      <Box baseline="none" flex alignCenterY fullWidth>
                        <Box
                          flex
                          alignCenterY
                          baseline="none"
                          fullWidth
                          spaceBetween
                          className={classes(pr8)}
                        >
                          <Text
                            className={classes(mr8, style({ userSelect: 'none' }))}
                            baseline="surface"
                            size="xs"
                            disableMediaQuery
                            lighter
                            shrink
                          >
                            {filter.label}
                          </Text>
                          <Text size="sm" disableMediaQuery shrink>
                            Click to select
                          </Text>
                        </Box>
                      </Box>
                    )
                  }}
                  renderValue={(data: any) => {
                    const tableColorCodes = base?.properties?.tableColorCode
                    const colors: any =
                      tableColorCodes?.key === filter.name ? tableColorCodes.options : {}

                    return (
                      <Box baseline="none" flex alignCenterY fullWidth>
                        <Box
                          flex
                          alignCenterY
                          baseline="none"
                          fullWidth
                          spaceBetween
                          className={classes(Object.keys(data).length > 0 && pr8)}
                        >
                          <Box
                            style={{
                              background: colors[data.label || data],
                              left: -12,
                              top: -8,
                              width: 2,
                              height: 32,
                              borderRadius: '4px 0 0 4px',
                            }}
                            absolute
                          />
                          <Text
                            className={classes(mr8, style({ userSelect: 'none' }))}
                            baseline="surface"
                            size="xs"
                            disableMediaQuery
                            lighter
                            shrink
                          >
                            {filter.label}
                          </Text>
                          <Text size="sm" disableMediaQuery shrink>
                            {data.label || data}
                          </Text>
                        </Box>
                      </Box>
                    )
                  }}
                  options={filter.options}
                  onChange={(data: any) => {
                    $equalTerms.set((prev: any) => {
                      return {
                        ...prev,
                        [filter.name]: data.name,
                      }
                    })
                    $rows.get({
                      search: { equal: { [filter.name]: data.name } },
                      updateGetParams: true,
                    })
                  }}
                  fixed={true}
                  value={value}
                />
              </Box>
            )
          })}
      </Box>
    )

    return (
      <Snippet>
        <Box baseline="none" row>
          <Box baseline="none" row>
            <Box
              className={classes(pl16, pr8)}
              minHeight={54}
              baseline="none"
              spaceBetween
              alignCenterY
            >
              <Box flex>
                <Dropdown
                  floatProps={{
                    anchorPosition: 'bottom left',
                    dialogPosition: 'top left',
                    fullWidth: false,
                  }}
                  className={classes(mr8, mr4mdi, hide, flexmd)}
                >
                  <Button
                    icon={
                      <span className={classes('material-icons', f16i)}>
                        {searchToggled ? 'close' : 'sort'}
                      </span>
                    }
                    label={GLOBAL_CONTENTS.filters[local]}
                    size="md"
                    baseline={
                      searchTermValue || Object.keys(equalTerms).length > 0 || rows?.search?.equal
                        ? 'secondary'
                        : 'background'
                    }
                  />
                  {() => {
                    return (
                      <Box baseline="surface" className={p8} shadow row>
                        {renderFilters(true)}
                      </Box>
                    )
                  }}
                </Dropdown>
                <Box className={classes(flex, hidemdi)}>{renderFilters()}</Box>
                <Button
                  label={GLOBAL_CONTENTS.reset[local]}
                  baseline="surface"
                  size="md"
                  onClick={resetSearch}
                />
              </Box>

              <Box flex baseline="none" alignCenterY>
                <Text
                  size="xs"
                  style={{
                    marginTop: 2,
                    color: 'rgb(120,120,120)',
                  }}
                  className={mr8}
                >
                  {`${GLOBAL_CONTENTS.page[local]} ${rows.payload.page || 1} of ${rows.payload
                    .totalPage || 1}`}
                </Text>
                <EvenMoreSimplerPagination data={rows} $data={$rows} size="sm" />
              </Box>
            </Box>
          </Box>
          <Line />
        </Box>
        {rows.payload.data.length > 0 && (
          <>
            <Table
              base={base}
              topOffset={getOffsetTop()}
              headers={[...internalHeaders]}
              data={rows.payload.data}
              actions={[...(actions ? actions.filter((a: any) => a.target === 'table-row') : [])]}
              refresh={() => {
                $searchTrigger.set(Date.now())
              }}
              isEditable={isEditable}
              isViewable={isViewable}
              isSortable={false}
              isClickSelectDisabled
              $selected={$selected}
              selected={selected}
              onEdit={async key => {
                await import('../../sidebars/example')
                Sidebar.push({
                  type: 'Example',
                  componentProps: {
                    row,
                    $row,
                    rows,
                    $rows,
                    selected: key,
                    $selected,
                    sync,
                    isEditable,
                    url,
                  },
                })
              }}
            />
          </>
        )}
        <Box rounded>
          {!rows.meta.isLoading && rows.payload.data?.length === 0 && (
            <Box row rounded>
              <Line className={mb8} />
              <Box className={classes(p16, pt24, pb32)} flex alignCenterX rounded>
                <Box flex alignCenterY alignCenterX rounded row>
                  <Text size="md">
                    {GLOBAL_CONTENTS.tableEmptyPrefix[local]}
                    {name.plural.toLowerCase()}
                    {GLOBAL_CONTENTS.tableEmptyPostfix[local]}
                  </Text>
                  <Box className={mt8}>
                    <Link to={`${location.pathname}/new`}>
                      <Button
                        label={`${GLOBAL_CONTENTS.addFormPrefix[local]}${name.singular}${GLOBAL_CONTENTS.addFormPostfix[local]}`}
                        baseline="primary"
                        square
                        size="sm"
                        icon={
                          <i className={classes(process.env.REACT_APP_GOOGLE_MATERIAL_ICONS, f18i)}>
                            add
                          </i>
                        }
                      />
                    </Link>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        <Box className={STYLES.containerTable} baseline="surface" rounded></Box>
      </Snippet>
    )
  }),
)

const STYLES = stylesheet({
  containerTable: {},
  buttonSearch: {
    width: 32,
    borderRadius: '4px 0 0 4px',
  },
})
